/**
 * CooperMembresManagement.js
 * Composant de gestion des membres pour les COOPER'S
 * Utilise le formulaire professionnel MembreCreationForm (copie conforme de DEVENIR MEMBRE)
 */
import React from 'react';
import { EnvoisKlarysSection } from '../klarys/EnvoisKlarysSection';

import { toast } from 'sonner';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AdhesionsCoppamBadges } from './AdhesionsCoppamBadges';

import { Users, UserPlus, FileText, CheckCircle, Mail, Phone, Building, MapPin, Calculator, Download, Shield, AlertTriangle, Award, Calendar, Clipboard, CreditCard } from 'lucide-react';

import ProfileCompletenessRadar from '../MembresTable/ProfileCompletenessRadar';

import MembreSouscriptionRCDS from '../MembreSouscriptionRCDS';

import DocumentSections from '../DocumentSections';

import { downloadAttestationFTPE } from '../../services/downloadService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _API = `${BACKEND_URL}/api`;

// V37.22 — Parser réutilisable pour l'import depuis presse-papier
const IMPORT_KEY_MAP = {
  'raison sociale': 'raison_sociale',
  'siret': 'siret',
  'prenom': 'prenom',
  'prénom': 'prenom',
  'nom': 'nom',
  'email': 'email',
  'telephone': 'telephone',
  'téléphone': 'telephone',
  'adresse': 'adresse',
  'code postal': 'code_postal',
  'ville': 'ville',
  'territoire': 'territoire',
  'statut juridique': 'statut_juridique',
};

// Nombre de champs uniques réellement reconnus (les clés avec/sans accent
// pointent vers le même champ)
const IMPORT_UNIQUE_FIELDS = new Set(Object.values(IMPORT_KEY_MAP)).size;

// Champs critiques affichés en chips de validation (ordre = priorité)
const IMPORT_CRITICAL_FIELDS = [
  { key: 'raison_sociale', label: 'Raison sociale' },
  { key: 'nom', label: 'Nom' },
  { key: 'prenom', label: 'Prénom' },
  { key: 'email', label: 'Email' },
  { key: 'telephone', label: 'Téléphone' },
  { key: 'territoire', label: 'Territoire' },
];

function parseImportText(text) {
  const out = {};
  (text || '').split(/\r?\n/).forEach((line) => {
    const idx = line.indexOf(':');
    if (idx <= 0) return;
    const rawKey = line.slice(0, idx).trim().toLowerCase();
    const value = line.slice(idx + 1).trim();
    if (!value) return;
    const mapped = IMPORT_KEY_MAP[rawKey];
    if (mapped) out[mapped] = value;
  });
  return out;
}

export default function CooperModals1({ ctx }) {
  const { fetchMembreDocuments, getStatusBadge, handleFTPEAdhesion, handleReprendreSimulation, handleViewDocuments, importParsed, importText, loadingDocuments, membreDocuments, selectedMembre, setImportText, setImportedInitialValues, setShowCreateModal, setShowDetailModal, setShowDocumentsModal, setShowImportModal, setShowSimulateurModal, showDetailModal, showDocumentsModal, showImportModal } = ctx;
  const { fetchMembres, getAuthHeader, setSelectedMembre } = ctx;
  return (
    <>

      {/* V37.20 — Modal Import depuis presse-papier */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clipboard className="h-5 w-5 text-emerald-600" />
              Importer une fiche depuis le presse-papier
            </DialogTitle>
            <DialogDescription>
              Collez ici la fiche copiée depuis « Mes Achats → Voir détails → Copier la fiche complète ».
              Les champs reconnus pré-rempliront le formulaire « Nouveau Membre ».
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <textarea
                data-testid="import-clipboard-textarea"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                onPaste={(e) => {
                  // V37.21 — Capture explicite du Ctrl+V au cas où la
                  // permission clipboard n'a pas été accordée à l'ouverture
                  const pasted = e.clipboardData?.getData('text');
                  if (pasted) {
                    e.preventDefault();
                    setImportText(pasted);
                  }
                }}
                placeholder={
                  "Collez ici votre fiche (Ctrl+V).\n\n" +
                  "Exemple :\n" +
                  "Raison sociale: Mar Bat Construction\n" +
                  "SIRET: 12345678901234\n" +
                  "Prénom: Marie\n" +
                  "Nom: Durand\n" +
                  "Email: marie@example.com\n" +
                  "Téléphone: +590 690906429\n" +
                  "Adresse: 12 rue des Lilas\n" +
                  "Code postal: 97160\n" +
                  "Ville: Le Moule\n" +
                  "Territoire: Guadeloupe"
                }
                rows={12}
                className="w-full font-mono text-sm bg-slate-50 border-2 border-slate-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-slate-900 placeholder:text-slate-400/60 placeholder:italic"
                autoFocus
              />
              {importText && (
                <div
                  className="absolute top-2 right-2 flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full"
                  data-testid="import-clipboard-live-status"
                  data-recognized={Object.keys(importParsed).length}
                  style={{
                    backgroundColor: Object.keys(importParsed).length >= IMPORT_CRITICAL_FIELDS.length
                      ? '#10b981' /* emerald-500 */
                      : Object.keys(importParsed).length >= 3
                      ? '#f59e0b' /* amber-500 */
                      : '#ef4444' /* red-500 */,
                    color: 'white',
                  }}
                >
                  {Object.keys(importParsed).length >= IMPORT_CRITICAL_FIELDS.length ? '✓' : '⚠'}
                  {' '}
                  {Object.keys(importParsed).length}/{IMPORT_UNIQUE_FIELDS} champs reconnus
                </div>
              )}
            </div>
            {/* V37.22 — Chips de validation des champs critiques en temps réel */}
            {importText && (
              <div
                className="flex flex-wrap gap-1.5"
                data-testid="import-clipboard-chips"
              >
                {IMPORT_CRITICAL_FIELDS.map((f) => {
                  const present = !!importParsed[f.key];
                  return (
                    <span
                      key={f.key}
                      className={
                        'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ' +
                        (present
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                          : 'bg-red-50 text-red-700 border-red-200')
                      }
                      data-testid={`import-chip-${f.key}`}
                      data-present={present ? 'true' : 'false'}
                    >
                      <span className={'w-1.5 h-1.5 rounded-full ' + (present ? 'bg-emerald-500' : 'bg-red-400')} />
                      {present ? '✓' : '○'} {f.label}
                    </span>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
              <span>
                Format attendu : <code className="bg-white px-1 rounded font-mono">Clé: valeur</code> sur chaque ligne.
                Clés reconnues : <strong>Raison sociale, SIRET, Prénom, Nom, Email, Téléphone, Adresse, Code postal, Ville, Territoire</strong>.
              </span>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowImportModal(false)}
              className="w-full sm:w-auto"
            >
              Annuler
            </Button>
            <Button
              data-testid="import-clipboard-submit"
              disabled={Object.keys(importParsed).length === 0}
              onClick={() => {
                // V37.22 — Réutilise le résultat du useMemo (déjà parsé en temps réel)
                const out = importParsed;
                const recognized = Object.keys(out).length;
                if (recognized === 0) {
                  toast.error('Aucun champ reconnu. Vérifiez le format « Clé: valeur ».');
                  return;
                }
                setImportedInitialValues(out);
                setShowImportModal(false);
                setShowCreateModal(true);
                toast.success(`${recognized} champ(s) importé(s). Vérifiez et complétez le formulaire.`);
              }}
              className="w-full sm:flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {Object.keys(importParsed).length === 0
                ? 'Collez du texte pour pré-remplir'
                : `Pré-remplir le formulaire (${Object.keys(importParsed).length} champs)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Détail Membre — Vue ultra pro */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0">
          {selectedMembre && (() => {
            const initials = `${selectedMembre.prenom?.[0] || ''}${selectedMembre.nom?.[0] || ''}`.toUpperCase() || 'M';
            const fullName = `${selectedMembre.prenom || ''} ${selectedMembre.nom || ''}`.trim() || selectedMembre.entreprise || 'Membre';
            const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : null;

            return (
              <>
                {/* Header gradient avec avatar XL */}
                <div className="px-6 pt-6 pb-5 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white">
                  <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2 text-xs font-medium opacity-90">
                      <Users className="h-4 w-4" /> Détails du membre
                    </DialogTitle>
                  </DialogHeader>
                  <div className="flex items-start gap-4 mt-3">
                    <div className="h-20 w-20 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center shadow-lg flex-shrink-0">
                      <span className="text-3xl font-bold text-white">{initials}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-2xl font-bold text-white leading-tight break-words">{fullName}</h3>
                      {selectedMembre.numero_membre && (
                        <p className="text-sm text-white/80 font-mono mt-1">{selectedMembre.numero_membre}</p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {getStatusBadge(selectedMembre.status)}
                        {selectedMembre.adhesion_ftpe && (
                          <Badge className="bg-amber-400 text-amber-900 font-semibold border-0">
                            <Award className="h-3 w-3 mr-1" /> FTPE Validé
                          </Badge>
                        )}
                        {selectedMembre.rcds_actif && (
                          <Badge className="bg-emerald-400 text-emerald-900 font-semibold border-0">
                            <Shield className="h-3 w-3 mr-1" /> RCDS Actif
                          </Badge>
                        )}
                      </div>
                      <AdhesionsCoppamBadges membreId={selectedMembre.id} />
                    </div>
                  </div>
                </div>

                <div className="px-6 py-5 space-y-5">
                  {/* Score de complétude — radar/spider */}
                  <ProfileCompletenessRadar
                    membre={selectedMembre}
                    onCompleteClick={() => {
                      setShowDetailModal(false);
                      // Délai pour la transition fluide + dispatch d'un event que MembresTable écoute
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('open-edit-contact', {
                          detail: { membreId: selectedMembre.id, membre: selectedMembre },
                        }));
                      }, 200);
                    }}
                  />

                  {/* Simulateur RCDS pré-rempli par le futur membre — À TRAITER */}
                  {selectedMembre.simulateur_rcds && (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="membre-simulateur-rcds">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                          <Shield className="h-4 w-4" /> Simulateur RCDS pré-rempli
                        </h4>
                        {(selectedMembre.simulateur_rcds_statut === 'a_traiter' || selectedMembre.simulateur_rcds.statut === 'a_traiter') && (
                          <Badge className="bg-amber-500 text-white border-0" data-testid="membre-simulateur-badge">À traiter</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div><p className="text-[10px] uppercase text-amber-600">CA HT</p><p className="font-medium">{selectedMembre.simulateur_rcds.ca_ht || '—'} €</p></div>
                        <div><p className="text-[10px] uppercase text-amber-600">Effectif</p><p className="font-medium">{selectedMembre.simulateur_rcds.effectif || '—'}</p></div>
                        <div><p className="text-[10px] uppercase text-amber-600">Masse salariale</p><p className="font-medium">{selectedMembre.simulateur_rcds.masse_salariale || '—'} €</p></div>
                        <div><p className="text-[10px] uppercase text-amber-600">Ancienneté</p><p className="font-medium">{selectedMembre.simulateur_rcds.anciennete || '—'}</p></div>
                      </div>
                      {Array.isArray(selectedMembre.simulateur_rcds.activites) && selectedMembre.simulateur_rcds.activites.length > 0 && (
                        <div className="mt-3">
                          <p className="text-[10px] uppercase text-amber-600 mb-1">Activités déclarées</p>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedMembre.simulateur_rcds.activites.map((a, i) => (
                              <span key={i} className="inline-flex px-2 py-0.5 rounded-full text-xs bg-white border border-amber-300 text-amber-800">{a.nom || a}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedMembre.souscription_rcds && (
                    <MembreSouscriptionRCDS selectedMembre={selectedMembre} setSelectedMembre={setSelectedMembre} getAuthHeader={getAuthHeader} fetchMembres={fetchMembres} />
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedMembre.email && (
                      <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200 hover:border-indigo-200 transition-colors">
                        <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Mail className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Email</p>
                          <a href={`mailto:${selectedMembre.email}`} className="text-sm font-medium text-gray-900 hover:text-blue-600 break-all">{selectedMembre.email}</a>
                        </div>
                      </div>
                    )}
                    {selectedMembre.telephone && (
                      <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200 hover:border-indigo-200 transition-colors">
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Phone className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Téléphone</p>
                          <a href={`tel:${selectedMembre.telephone}`} className="text-sm font-medium text-gray-900 hover:text-emerald-600">{selectedMembre.telephone}</a>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Entreprise */}
                  {(selectedMembre.entreprise || selectedMembre.siret) && (
                    <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/30 to-white overflow-hidden">
                      <div className="px-4 py-2.5 bg-gradient-to-r from-indigo-50 to-purple-50/40 border-b border-indigo-100 flex items-center gap-2">
                        <Building className="h-4 w-4 text-indigo-600" />
                        <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Entreprise</span>
                      </div>
                      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                        {selectedMembre.entreprise && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Raison sociale</p>
                            <p className="text-sm font-semibold text-gray-900 break-words">{selectedMembre.entreprise}</p>
                          </div>
                        )}
                        {selectedMembre.siret && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">SIRET</p>
                            <p className="text-sm font-mono font-medium text-gray-900">{selectedMembre.siret}</p>
                          </div>
                        )}
                        {selectedMembre.statut_juridique && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Statut juridique</p>
                            <p className="text-sm font-medium text-gray-900">{selectedMembre.statut_juridique}</p>
                          </div>
                        )}
                        {selectedMembre.code_naf && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Code APE / NAF</p>
                            <p className="text-sm font-mono font-medium text-gray-900">{selectedMembre.code_naf}</p>
                          </div>
                        )}
                        {selectedMembre.effectif !== undefined && selectedMembre.effectif !== null && selectedMembre.effectif !== '' && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Effectif</p>
                            <p className="text-sm font-medium text-gray-900">{selectedMembre.effectif} salarié(s)</p>
                          </div>
                        )}
                        {(selectedMembre.chiffre_affaires !== undefined && selectedMembre.chiffre_affaires !== null && selectedMembre.chiffre_affaires !== '') && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Chiffre d'affaires</p>
                            <p className="text-base font-bold text-emerald-700">{parseFloat(selectedMembre.chiffre_affaires || 0).toLocaleString('fr-FR')} €</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Adresse & Territoire */}
                  {(selectedMembre.adresse || selectedMembre.territoire || selectedMembre.pays) && (
                    <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50/30 to-white overflow-hidden">
                      <div className="px-4 py-2.5 bg-gradient-to-r from-emerald-50 to-teal-50/40 border-b border-emerald-100 flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-emerald-600" />
                        <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Adresse &amp; Territoire</span>
                      </div>
                      <div className="p-4 space-y-3">
                        {selectedMembre.adresse && (
                          <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Adresse</p>
                            <p className="text-sm font-medium text-gray-900">{selectedMembre.adresse}</p>
                            {(selectedMembre.code_postal || selectedMembre.ville) && (
                              <p className="text-sm text-gray-700 mt-0.5">
                                {[selectedMembre.code_postal, selectedMembre.ville].filter(Boolean).join(' ')}
                                {selectedMembre.pays ? ` — ${selectedMembre.pays}` : ''}
                              </p>
                            )}
                          </div>
                        )}
                        {selectedMembre.territoire && (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-emerald-700 text-white border-emerald-900 font-semibold">
                              Territoire : {selectedMembre.territoire}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Métadonnées de création */}
                  {(selectedMembre.created_at || selectedMembre.date_creation || selectedMembre.created_by_name) && (
                    <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                      {(selectedMembre.created_at || selectedMembre.date_creation) && (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          Créé le <strong className="text-gray-700">{formatDate(selectedMembre.created_at || selectedMembre.date_creation) || '-'}</strong>
                        </span>
                      )}
                      {selectedMembre.created_by_name && (
                        <span className="flex items-center gap-1.5">
                          <Users className="h-3 w-3 text-gray-400" />
                          Par <strong className="text-gray-700">{selectedMembre.created_by_name}</strong>
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions rapides */}
                  <div className="border-t pt-4">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Actions rapides</h4>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => {
                          setShowDetailModal(false);
                          setShowSimulateurModal(true);
                        }}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md gap-2"
                      >
                        <Calculator className="h-4 w-4" />
                        Nouvelle simulation
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDetailModal(false);
                          handleViewDocuments(selectedMembre);
                        }}
                        className="gap-2"
                      >
                        <FileText className="h-4 w-4" />
                        Voir les documents
                      </Button>
                      <Button
                        onClick={() => handleFTPEAdhesion(selectedMembre)}
                        className={`gap-2 shadow-md ${
                          selectedMembre?.adhesion_ftpe
                            ? 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700'
                            : selectedMembre?.adhesion_ftpe_en_cours
                              ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
                              : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                        }`}
                        disabled={selectedMembre?.adhesion_ftpe}
                      >
                        <Shield className="h-4 w-4" />
                        {selectedMembre?.adhesion_ftpe
                          ? 'FTPE Validé ✓'
                          : selectedMembre?.adhesion_ftpe_en_cours
                            ? 'FTPE En cours...'
                            : 'FTPE Membre'
                        }
                      </Button>
                    </div>

                    {/* Info FTPE avec date d'expiration */}
                    {selectedMembre?.adhesion_ftpe && selectedMembre?.ftpe_expiration_date && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-700 flex items-center gap-1 mb-2">
                          <CheckCircle className="h-4 w-4" />
                          Adhésion FTPE valide jusqu&apos;au {new Date(selectedMembre.ftpe_expiration_date).toLocaleDateString('fr-FR')}
                        </p>
                        {selectedMembre?.ftpe_adhesion_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-500 text-green-700 hover:bg-green-100"
                            onClick={() => {
                              downloadAttestationFTPE(selectedMembre.ftpe_adhesion_id);
                              toast.success('Téléchargement de l\'attestation FTPE...');
                            }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Télécharger l&apos;attestation FTPE
                          </Button>
                        )}
                      </div>
                    )}
                    {selectedMembre?.adhesion_ftpe && selectedMembre?.ftpe_expiration_date &&
                      new Date(selectedMembre.ftpe_expiration_date) < new Date() && (
                      <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-700 flex items-center gap-1">
                          <AlertTriangle className="h-4 w-4" />
                          Adhésion FTPE expirée - À renouveler
                        </p>
                      </div>
                    )}
                  </div>
                  <EnvoisKlarysSection membreId={selectedMembre?.id} />
                </div>

                <DialogFooter className="px-6 py-3 border-t bg-gray-50">
                  <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                    Fermer
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modal Documents et Simulations */}
      <Dialog open={showDocumentsModal} onOpenChange={setShowDocumentsModal}>
        <DialogContent className="max-w-6xl w-[96vw] max-h-[90vh] overflow-y-auto p-0 gap-0">
          {/* Header gradient pro */}
          <div className="px-6 pt-5 pb-4 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-b border-indigo-100">
            <DialogHeader className="space-y-1">
              <DialogTitle className="flex items-center gap-3 text-base">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md flex-shrink-0">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold text-gray-900 leading-tight">Documents &amp; Simulations</p>
                  <p className="text-xs text-gray-600 mt-0.5 truncate">
                    {selectedMembre ? <>Documents de <span className="font-semibold text-gray-800">{selectedMembre.prenom} {selectedMembre.nom}</span></> : 'Membre'}
                  </p>
                </div>
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="px-6 pb-4">
            {loadingDocuments ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : membreDocuments ? (
              <div className="space-y-5 py-4">
                <div className="text-xs text-gray-600 bg-gradient-to-br from-slate-50 to-blue-50/50 p-4 rounded-xl border border-blue-100">
                  <p className="font-semibold text-gray-700 mb-1.5">Règles d'affichage :</p>
                  <ul className="list-disc list-inside space-y-0.5 text-gray-600">
                    <li>Simulations en cours : visibles pendant <strong>30 jours</strong></li>
                    <li>Attestations FTPE : visibles jusqu'à la <strong>date d'échéance</strong></li>
                    <li>Contrats signés (RCDS) : visibles jusqu'à leur <strong>date d'échéance</strong></li>
                  </ul>
                </div>

                <DocumentSections
                  membreDocuments={membreDocuments}
                  membreId={selectedMembre?.id}
                  onResume={handleReprendreSimulation}
                  onRefresh={() => selectedMembre && fetchMembreDocuments(selectedMembre.id)}
                />
              </div>
            ) : null}
          </div>

          <DialogFooter className="px-6 py-3 border-t bg-gray-50">
            <Button variant="outline" onClick={() => setShowDocumentsModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
